"use client";

import Image from "next/image";
import { useId, useState } from "react";
import { Link } from "@/i18n/navigation";
import { LinkButton } from "@/components/ui/Button";
import { ArrowIcon, ChevronDownIcon, PinIcon } from "@/components/icons/UiIcons";

export type ItineraryView = {
  id: string;
  days: number;
  title: string;
  intro: string;
  image: string;
  schedule: {
    title: string;
    intro: string;
    stops: { slug?: string; title: string; note: string; image?: string }[];
  }[];
};

/**
 * Tabbed 1 / 2 / 3-day plans. Tabs follow the WAI-ARIA tabs pattern (arrow
 * keys move between them); days are native <details> so long plans fold on
 * mobile and still work without JavaScript. All text arrives localized.
 * Every panel is rendered (inactive ones carry the `hidden` attribute) so the
 * full content of all plans is in the server HTML for crawlers.
 */
export function ItinerarySelector({
  itineraries,
  dayLabels,
  flexibleNote,
  ctaLabel,
  ctaButton,
  tabsLabel,
}: {
  itineraries: ItineraryView[];
  /** "Day 1", "Day 2", … pre-translated on the server (index = day - 1). */
  dayLabels: string[];
  flexibleNote: string;
  /** Heading of the stay-with-us card. */
  ctaLabel: string;
  /** Button text of the stay-with-us card. */
  ctaButton: string;
  tabsLabel: string;
}) {
  const [index, setIndex] = useState(0);
  const base = useId();
  const dayLabel = (n: number) => dayLabels[n - 1] ?? String(n);

  function onKey(e: React.KeyboardEvent<HTMLButtonElement>, i: number) {
    const last = itineraries.length - 1;
    let next: number | null = null;
    if (e.key === "ArrowRight") next = i === last ? 0 : i + 1;
    else if (e.key === "ArrowLeft") next = i === 0 ? last : i - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next == null) return;
    e.preventDefault();
    setIndex(next);
    document.getElementById(`${base}-tab-${next}`)?.focus();
  }

  return (
    <div>
      <div
        role="tablist"
        aria-label={tabsLabel}
        className="grid gap-3 sm:grid-cols-3"
      >
        {itineraries.map((it, i) => {
          const active = i === index;
          return (
            <button
              key={it.id}
              id={`${base}-tab-${i}`}
              role="tab"
              type="button"
              aria-selected={active}
              aria-controls={`${base}-panel-${i}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setIndex(i)}
              onKeyDown={(e) => onKey(e, i)}
              className={`card-lift group relative flex items-center gap-4 overflow-hidden rounded-2xl border p-3 text-left transition-colors ${
                active
                  ? "border-forest-500 bg-forest-500 text-white"
                  : "border-sand bg-cream text-bark"
              }`}
            >
              <span className="relative h-16 w-20 shrink-0 overflow-hidden rounded-xl">
                <Image
                  src={it.image}
                  alt={it.title}
                  fill
                  sizes="80px"
                  className="object-cover"
                />
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-[11px] font-semibold uppercase tracking-[0.16em] ${
                    active ? "text-copper-light" : "text-clay-dark"
                  }`}
                >
                  {dayLabel(it.days)}
                </span>
                <span className="mt-0.5 block font-display text-lg font-bold leading-tight">
                  {it.title}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {itineraries.map((it, i) => (
        <div
          key={it.id}
          id={`${base}-panel-${i}`}
          role="tabpanel"
          aria-labelledby={`${base}-tab-${i}`}
          hidden={i !== index}
          className="mt-8"
        >
          <div className="grid gap-8 lg:grid-cols-[1fr_minmax(0,320px)]">
            <div>
              <p className="max-w-2xl text-base leading-relaxed text-stone sm:text-lg">
                {it.intro}
              </p>
              <div className="mt-6 space-y-4">
                {it.schedule.map((day, d) => (
                  <details
                    key={d}
                    open={d === 0 || it.schedule.length === 1}
                    className="group rounded-2xl border border-sand bg-cream"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
                      <span>
                        <span className="eyebrow">{dayLabel(d + 1)}</span>
                        <span className="mt-1 block font-display text-xl font-bold text-bark">
                          {day.title}
                        </span>
                      </span>
                      <ChevronDownIcon className="details-chevron h-5 w-5 shrink-0 text-stone" />
                    </summary>
                    <div className="border-t border-sand px-5 pb-5 pt-4">
                      <p className="text-sm leading-relaxed text-stone">{day.intro}</p>
                      <ol className="mt-4 space-y-3">
                        {day.stops.map((s, k) => (
                          <li key={k} className="flex gap-3">
                            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-forest-50 text-xs font-bold text-forest-600">
                              {k + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              {s.slug ? (
                                <Link
                                  href={`/discover/${s.slug}`}
                                  className="inline-flex items-center gap-1.5 font-semibold text-bark hover:text-forest-600"
                                >
                                  <PinIcon className="h-4 w-4 text-forest-400" />
                                  {s.title}
                                  <ArrowIcon className="h-3.5 w-3.5 text-forest-400" />
                                </Link>
                              ) : (
                                <span className="font-semibold text-bark">{s.title}</span>
                              )}
                              <p className="mt-0.5 text-sm leading-relaxed text-stone">
                                {s.note}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </details>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-stone">{flexibleNote}</p>
            </div>

            <aside className="on-forest h-fit rounded-2xl p-6 lg:sticky lg:top-24">
              <div className="relative mb-4 aspect-[4/3] overflow-hidden rounded-xl">
                <Image
                  src={it.image}
                  alt={it.title}
                  fill
                  loading={i === index ? "eager" : "lazy"}
                  sizes="(max-width: 1024px) 100vw, 320px"
                  className="object-cover"
                />
              </div>
              <p className="font-display text-xl font-bold">{ctaLabel}</p>
              <LinkButton href="/stays" variant="clay" className="mt-4 w-full">
                {ctaButton}
              </LinkButton>
            </aside>
          </div>
        </div>
      ))}
    </div>
  );
}
